import Box from '@ahamove/web-components/lib/Box';
import Button from '@ahamove/web-components/lib/Button';
import Divider from '@ahamove/web-components/lib/Divider';
import Grid from '@ahamove/web-components/lib/Grid';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import type { StackProps } from '@ahamove/web-components/lib/Stack';
import Stack from '@ahamove/web-components/lib/Stack';
import Typography from '@ahamove/web-components/lib/Typography';
import useTheme from '@ahamove/web-components/lib/useTheme';
import { vi, enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import type { RangeKeyDict } from 'react-date-range';
import { DateRangePicker } from 'react-date-range';
import type { ServiceModalData } from '@/api/service/types';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import type { DateTimeStore } from '@/global-stores/useDateTimeStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { getValidStartTime } from '@/lib/common';
import {
  DEFAULT_DATE_TIME_FORMAT,
  DEFAULT_MAXIMUM_START_TIME_FROM_NOW_IN_DAY,
  DEFAULT_MINIMUM_RENT_TIME_IN_SECOND,
  DEFAULT_START_TIME,
  DEFAULT_TIME_RANGE_OBJ,
  RentType,
} from '@/lib/constants';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { useNewBooking } from '../NewBookingProvider';
import DateTimeRangeCalculation from './DateTimeRangeCalculation';
import StyledReactDateRangeWrapper from './StyledReactDateRangeWrapper';
import TimeSlider from './TimeSlider';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css';

type Props = Omit<StackProps, 'onSubmit'> & {
  activeTab: number;
  onSubmit: (value: DateTimeStore) => void;
  serviceDetail?: ServiceModalData;
};

export default function FlexibleDatePicker({
  activeTab,
  onSubmit,
  serviceDetail,
  ...props
}: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const { rentType } = useRentTypeStore();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { locale, pathname } = useRouter();
  const now = dayjs();

  const { startTime, expectedEndTime } = useDateTimeStore();
  const { serviceModels } = useNewBooking();

  const service = React.useMemo(() => {
    if (pathname.includes('bookings') && serviceDetail) {
      return serviceDetail;
    }
    if (serviceModels?.length) {
      return serviceModels[0];
    }
    return null;
  }, [serviceModels, serviceDetail, pathname]);

  const DEFAULT_TIME_RANGE: number[] = React.useMemo(() => {
    return DEFAULT_TIME_RANGE_OBJ[
      rentType as keyof typeof DEFAULT_TIME_RANGE_OBJ
    ];
  }, []);

  const timeConfig = React.useMemo(
    () =>
      // 4 * 60 * 60,
      (service && service.configs.minimum_start_time_from_now_in_second) ?? 0,
    [service]
  );

  const minimumRentTime = React.useMemo(
    () =>
      dayjs.duration(
        // 1 * 60 * 60,
        (service && service.configs.minimum_rent_time_in_second) ??
          DEFAULT_MINIMUM_RENT_TIME_IN_SECOND,
        'second'
      ),
    [service]
  );

  const maxRentTimeInDate = React.useMemo(
    () =>
      dayjs.duration(
        (service && service?.configs?.maximum_rent_time_in_day) ?? 0,
        'day'
      ),
    [service]
  );

  const defaultStartTime = React.useMemo(() => {
    const res =
      startTime > 0 && startTime - now.unix() > timeConfig
        ? startTime
        : getValidStartTime(
            dayjs()
              .startOf('day')
              .set('date', dayjs().date() + 1),
            timeConfig,
            DEFAULT_TIME_RANGE
          );
    return res;
  }, [startTime, timeConfig, now]);

  const defaultEndTime = React.useMemo(
    () =>
      expectedEndTime > 0 &&
      expectedEndTime - defaultStartTime >= minimumRentTime.asSeconds()
        ? expectedEndTime
        : getValidStartTime(
            dayjs()
              .startOf('day')
              .set('date', dayjs().date() + 3),
            timeConfig,
            DEFAULT_TIME_RANGE
          ),
    [expectedEndTime, defaultStartTime, timeConfig]
  );

  const [startUnix, setStartUnix] = React.useState<number>(defaultStartTime);
  const [endUnix, setEndUnix] = React.useState<number>(defaultEndTime);

  const minSelectableDate: dayjs.Dayjs = React.useMemo(() => {
    const nowD = dayjs();
    const dateToValidate = nowD.add(timeConfig, 'second');

    if (dateToValidate.hour() > DEFAULT_TIME_RANGE[1]) {
      return nowD.startOf('day').set('date', nowD.date() + 1);
    }

    return dayjs.unix(
      getValidStartTime(dayjs(), timeConfig, DEFAULT_TIME_RANGE)
    );
  }, [timeConfig, minimumRentTime]);

  const maxSelectableDate: dayjs.Dayjs = React.useMemo(() => {
    const minusTime =
      minimumRentTime.asHours() > timeConfig / 3600
        ? minimumRentTime.asHours()
        : timeConfig / 3600;
    const validateTime = DEFAULT_TIME_RANGE[1] - minusTime;
    const now = dayjs();

    let time =
      (service && service?.configs.maximum_start_time_from_now_in_day) ??
      DEFAULT_MAXIMUM_START_TIME_FROM_NOW_IN_DAY;
    if (rentType === RentType.CAR && now.hour() < validateTime) {
      time = time - 1;
    }
    return dayjs().add(time, 'day');
  }, [service, rentType]);

  const disableSubmit = React.useMemo(
    () => !startUnix || !endUnix,
    [startUnix, endUnix]
  );

  const handleChangeDateRange = (rangesByKey: RangeKeyDict) => {
    const { startDate, endDate } = rangesByKey.selection;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    const newStartTime = getValidStartTime(
      start,
      timeConfig,
      DEFAULT_TIME_RANGE
    );
    setStartUnix(newStartTime);

    if (end.isSame(start, 'date')) {
      const newStart = dayjs.unix(newStartTime);
      const newStartHour = newStart.hour();
      const endHour = end.hour();

      if (
        newStartHour + minimumRentTime.hours() > DEFAULT_TIME_RANGE[1] &&
        newStart.add(minimumRentTime.hours(), 'hour').hour() <
          DEFAULT_TIME_RANGE[0]
      ) {
        setEndUnix(
          start
            .startOf('day')
            .set('date', start.date() + 1)
            .set('hour', DEFAULT_START_TIME.hour)
            .set('minute', DEFAULT_START_TIME.minute)
            .unix()
        );
        return;
      }

      if (endHour < newStartHour + minimumRentTime.hours()) {
        const newStartToValidate = newStart.add(
          minimumRentTime.hours(),
          'hour'
        );
        if (
          (newStartToValidate.hour() > DEFAULT_TIME_RANGE[1] ||
            (newStartToValidate.hour() === DEFAULT_TIME_RANGE[1] &&
              newStartToValidate.minute() > 0)) &&
          !(
            newStartToValidate.month() === maxSelectableDate.month() &&
            newStartToValidate.date() >= maxSelectableDate.date()
          )
        ) {
          setEndUnix(
            newStart
              .startOf('day')
              .set('date', newStart.date() + 1)
              .set('hour', DEFAULT_START_TIME.hour)
              .set('minute', DEFAULT_START_TIME.minute)
              .unix()
          );
        } else {
          setEndUnix(
            newStart.set('hour', newStartHour + minimumRentTime.hours()).unix()
          );
        }
        return;
      }

      if (endHour > DEFAULT_TIME_RANGE[1]) {
        setEndUnix(newStart.set('hour', DEFAULT_TIME_RANGE[1]).unix());
        return;
      }

      setEndUnix(getValidStartTime(end, timeConfig, DEFAULT_TIME_RANGE));
    } else if (
      maxRentTimeInDate.asDays() &&
      end.startOf('days').diff(start.startOf('days'), 'days') >
        maxRentTimeInDate.asDays() - 1
    ) {
      const newStart = dayjs.unix(newStartTime);

      setEndUnix(
        newStart
          .startOf('day')
          .set('date', newStart.date() + (maxRentTimeInDate.asDays() - 1))
          .set('hour', DEFAULT_START_TIME.hour)
          .set('minute', DEFAULT_START_TIME.minute)
          .unix()
      );
    } else {
      setEndUnix(getValidStartTime(end, timeConfig, DEFAULT_TIME_RANGE));
    }
    // }
  };

  React.useEffect(() => {
    const startDate = dayjs.unix(startUnix);
    const endDate = dayjs.unix(endUnix);
    if (
      endDate.isSame(startDate, 'date') &&
      endDate.diff(startDate, 'second') / 3600 < minimumRentTime.hours()
    ) {
      const startDateToValidate = startDate.add(
        minimumRentTime.hours(),
        'hour'
      );
      if (
        (startDateToValidate.hour() > DEFAULT_TIME_RANGE[1] ||
          (startDateToValidate.hour() === DEFAULT_TIME_RANGE[1] &&
            startDateToValidate.minute() > 0)) &&
        !(
          startDateToValidate.month() === maxSelectableDate.month() &&
          startDateToValidate.date() >= maxSelectableDate.date()
        )
      ) {
        setEndUnix(
          startDate
            .startOf('day')
            .set('date', startDate.date() + 1)
            .set('hour', DEFAULT_START_TIME.hour)
            .set('minute', DEFAULT_START_TIME.minute)
            .unix()
        );
      } else {
        setEndUnix(
          startDate
            .set('hour', startDate.hour() + minimumRentTime.hours())
            .unix()
        );
      }
    }
  }, [startUnix]);

  return (
    <Stack direction={'column'} spacing={3} {...props}>
      <Typography variant={isMobile ? 'h3' : 'h2'}>
        {t('booking:datetime_picker.select_date_time')}
      </Typography>
      <StyledReactDateRangeWrapper>
        <DateRangePicker
          locale={locale === 'vi' ? vi : enUS}
          monthDisplayFormat="MMMM yyyy"
          showDateDisplay={false}
          direction="horizontal"
          months={isMobile ? 1 : 2}
          ranges={[
            {
              startDate: dayjs.unix(startUnix).toDate(),
              endDate: dayjs.unix(endUnix).toDate(),
              key: 'selection',
            },
          ]}
          onChange={handleChangeDateRange}
          rangeColors={[theme.palette.primary.main]}
          minDate={minSelectableDate.toDate()}
          maxDate={maxSelectableDate.toDate()}
        />
      </StyledReactDateRangeWrapper>
      <Divider />
      <Box sx={{ pb: isMobile ? 18 : 0 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" textTransform="uppercase" color="grey.900">
              {t('booking:datetime_picker.pickup')}
            </Typography>
            <Typography variant="body1" color="grey.600" mb={1}>
              {dayjs.unix(startUnix).format(DEFAULT_DATE_TIME_FORMAT)}
            </Typography>
            <TimeSlider
              isFlexibleDate
              timeConfig={timeConfig}
              minHourConfig={minimumRentTime.hours()}
              maxSelectableDate={maxSelectableDate}
              type="start"
              endDate={dayjs.unix(endUnix)}
              value={startUnix}
              onChange={(newValue) => setStartUnix(newValue)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" textTransform="uppercase" color="grey.900">
              {t('booking:datetime_picker.return')}
            </Typography>
            <Typography variant="body1" color="grey.600" mb={1}>
              {dayjs.unix(endUnix).format('HH:mm, DD/MM/YYYY')}
            </Typography>
            <TimeSlider
              isFlexibleDate
              timeConfig={timeConfig}
              minHourConfig={minimumRentTime.hours()}
              startDate={dayjs.unix(startUnix)}
              value={endUnix}
              onChange={(newValue) => setEndUnix(newValue)}
            />
          </Grid>
        </Grid>
      </Box>
      <Divider
        sx={{ mx: isMobile ? '-16px !important' : '-24px !important' }}
      />
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        justifyContent={isMobile ? 'center' : 'flex-end'}
        sx={
          isMobile
            ? {
                width: '100%',
                p: 2,
                bgcolor: 'common.white',
                position: 'fixed',
                bottom: 0,
                left: 0,
                zIndex: 30,
                borderTopWidth: 1,
                borderTopStyle: 'solid',
                borderTopColor: 'grey.100',
              }
            : {}
        }
      >
        <ListItemText
          sx={{ m: 0 }}
          primary={
            <>
              {t('booking:datetime_picker.duration')}
              <Typography
                variant="h3"
                color="primary.main"
                display="inline-block"
                ml={0.75}
                textTransform="lowercase"
              >
                <DateTimeRangeCalculation
                  type={rentType}
                  range={[startUnix, endUnix]}
                />
              </Typography>
            </>
          }
          primaryTypographyProps={{
            variant: 'h6',
            color: 'grey.900',
            sx: {
              textTransform: 'uppercase',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: isMobile ? 'space-between' : 'flex-start',
            },
          }}
          secondary={
            rentType === RentType.BIKE
              ? Math.round((endUnix - startUnix) / 3600) % 24 > 7
                ? t(`booking:datetime_picker.duration_note_bike`)
                : null
              : t(`booking:datetime_picker.duration_note_car`)
          }
          secondaryTypographyProps={{
            variant: 'caption',
            color: 'secondary.dark',
            mt: '2px !important',
          }}
        />

        <Button
          disabled={disableSubmit}
          fullWidth={isMobile}
          rounded
          sx={(theme) => ({
            ...theme.typography.h3,
            minHeight: 56,
            minWidth: 200,
          })}
          onClick={() => {
            onSubmit({
              startTime: startUnix,
              expectedEndTime: endUnix,
              fareType: 'dynamic_fare',
            });
          }}
          data-test-id="selectRentTimeBtn"
        >
          {t('booking:done')}
        </Button>
      </Stack>
    </Stack>
  );
}
